import { observer } from "mobx-react-lite"

import type { Sort } from "@/domains/table-processor"
import { ShowWhenTrue } from "@/components/conditionals"
import { Pagination } from "@/components/pagination"
import { Compound as Table } from "@/components/table"
import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { ClFarmStore } from "@/domains/cl-farm/cl-farm-store"
import { ClFarmAlmType } from "@/domains/core/types"
import { ThApr } from "@/domains/pools/components/table-headers/th-apr"
import { ThDisplayName } from "@/domains/pools/components/table-headers/th-display-name"
import { ThMyPool } from "@/domains/pools/components/table-headers/th-my-pool"
import { ThMyStaked } from "@/domains/pools/components/table-headers/th-my-staked"
import { ThStaked } from "@/domains/pools/components/table-headers/th-staked"
import { ThTvlUsd } from "@/domains/pools/components/table-headers/th-tvl-usd"
import { ThWalletBalanceValueUsd } from "@/domains/pools/components/table-headers/wallet-balance-value-usd"
import { paginate } from "@/domains/table-processor"
import { useChainConfig } from "@/hooks/use-chain-config"

import { ClFarmsTableRowContent } from "./cl-farms-table-row"
import { clFarmsProcessor } from "./clfarms-processor"

export const ClFarmsTable = observer(() => {
  const scaleApp = useScaleApplication()
  const chainConfig = useChainConfig()

  const alm = scaleApp.Pages.ClFarms.alm
  const currentPageNumber = scaleApp.Pages.ClFarms.currentPageNumber
  const setCurrentPageNumber = (value: number) => scaleApp.Pages.ClFarms.setCurrentPageNumberFromUi(value)
  const pageSize = scaleApp.Pages.ClFarms.pageSize
  const setPageSize = (value: number) => scaleApp.Pages.ClFarms.setPageSizeFromUi(value)
  const sortBy = scaleApp.Pages.ClFarms.sortBy
  const setSortBy = (value: Sort) => scaleApp.Pages.ClFarms.setSortByFromUi(value)
  const isCompact = !scaleApp.Pages.ClFarms.isDetailed
  const search = scaleApp.Pages.ClFarms.search
  const stakedOnly = scaleApp.Pages.ClFarms.stakedOnly

  const clFarms = scaleApp.ClFarms.Repository.getAll()
  const processed = clFarmsProcessor(clFarms, search, stakedOnly, alm as ClFarmAlmType, sortBy)
  const paginated = paginate({
    currentPageNumber,
    pageSize,
    numberOfRows: processed.rows.length,
  })

  const hasTableRows = processed.rows.length > 0

  return (
    <TooltipProvider>
      <Table.Root>
        <Table.Head>
          <Table.HeaderRow>
            <Table.Th className="max-w-[80px] px-0 sm:px-0" classNameInner="justify-center" isSortDisabled={true}>
              &nbsp;
            </Table.Th>
            <ThDisplayName setSortBy={setSortBy} sortBy={sortBy} name="Pair" colSpan={1} />
            <ThApr setSortBy={setSortBy} sortBy={sortBy} />
            <ThTvlUsd setSortBy={setSortBy} sortBy={sortBy} />
            <ThStaked setSortBy={setSortBy} sortBy={sortBy} />
            <ThWalletBalanceValueUsd setSortBy={setSortBy} sortBy={sortBy} />
            <ThMyPool setSortBy={setSortBy} sortBy={sortBy} />
            <ThMyStaked setSortBy={setSortBy} sortBy={sortBy} />
          </Table.HeaderRow>
        </Table.Head>
        <Table.Body>
          <ShowWhenTrue when={!hasTableRows}>
            <Table.Tr>
              <Table.Td colSpan={7} className="py-6 text-center">
                No pools found
              </Table.Td>
            </Table.Tr>
          </ShowWhenTrue>
          <ShowWhenTrue when={hasTableRows}>
            {processed.rows
              .slice(paginated.indexes.firstItemIndexOnPage, paginated.indexes.lastItemIndexOnPage)
              .map((pool: ClFarmStore) => (
                <ClFarmsTableRowContent
                  pool={pool}
                  chainSlug={chainConfig.slug}
                  key={pool.address.toString()}
                  isCompact={isCompact}
                />
              ))}
          </ShowWhenTrue>
        </Table.Body>
      </Table.Root>

      <Pagination
        paginated={paginated}
        onChangeRowsPerPage={(rowsPerPage) => {
          setPageSize(rowsPerPage)
          setCurrentPageNumber(1)
        }}
        onClickPreviousPage={(previousPageNumber) => setCurrentPageNumber(previousPageNumber)}
        onClickNextPage={(nextPageNumber) => setCurrentPageNumber(nextPageNumber)}
      />
    </TooltipProvider>
  )
})
